//

import {
  EditSkuImportDraftAction,
  ErrorKey,
  LoadingKey,
  PostSkuImportDraftAction,
  ProductsActions,
  ProductsState,
  SetConnectionsAction,
  SetErrorAction,
  SetFilteredConnectionsAction,
  SetFilteredLocalProductsAction,
  SetFilteredRemoteProductsAction,
  SetInitializedAction,
  SetLoadingAction,
  SetLocalParentChildProductsAction,
  SetLocalProductsAction,
  SetRemoteFormProductsAction,
  SetRemoteFormProductsWithImportsAction,
  SetRemoteProductsAction,
  SetServiceProductsAction,
  SetSkuImportDraftsAction,
} from "./types";
//
import { FormProduct, Product, ProductConnection, SkuImportDraftState } from "@shared/types";

export function setInitialized(initialized: boolean): SetInitializedAction {
  return {
    type: "SET_INITIALIZED",
    payload: {
      initialized,
    },
  };
}

export function setLoading(loadingKey: LoadingKey, loading: boolean): SetLoadingAction {
  return {
    type: "SET_LOADING",
    payload: {
      loadingKey: loadingKey,
      loading: loading,
    },
  };
}

export function setError(errorKey: ErrorKey, error: Error | undefined): SetErrorAction {
  return {
    type: "SET_ERROR",
    payload: { errorKey: errorKey, error: error },
  };
}

// local products

export function setLocalProducts(localProducts: Product[]): SetLocalProductsAction {
  return {
    type: "SET_LOCAL_PRODUCTS",
    payload: { localProducts },
  };
}

export function setFilteredLocalProducts(filteredLocalProducts: Product[]): SetFilteredLocalProductsAction {
  return {
    type: "SET_FILTERED_LOCAL_PRODUCTS",
    payload: { filteredLocalProducts },
  };
}

// remote products (shopify or woocommerce)
export function setRemoteProducts(remoteProducts: Product[]): SetRemoteProductsAction {
  return {
    type: "SET_REMOTE_PRODUCTS",
    payload: { remoteProducts },
  };
}

// all remote formProducts
export function setAllRemoteFormProducts(allRemoteFormProducts: FormProduct[]): SetRemoteFormProductsAction {
  return {
    type: "SET_REMOTE_FORM_PRODUCTS",
    payload: { remoteFormProducts: allRemoteFormProducts },
  };
}

export function setRemoteFormProductsWithImports(remoteFormProductsWithImports: FormProduct[]): SetRemoteFormProductsWithImportsAction {
  return {
    type: "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS",
    payload: { remoteFormProductsWithImports },
  };
}
export function setFilteredRemoteProducts(filteredRemoteProducts: Product[]): SetFilteredRemoteProductsAction {
  return {
    type: "SET_FILTERED_REMOTE_PRODUCTS",
    payload: { filteredRemoteProducts },
  };
}
// sku import drafts
export function setSkuImportDrafts(skuImportDrafts: FormProduct[]): SetSkuImportDraftsAction {
  return {
    type: "SET_SKU_IMPORT_DRAFTS",
    payload: { skuImportDrafts },
  };
}

export function addSkuImportDraft(skuImportDraft: FormProduct): PostSkuImportDraftAction {
  return {
    type: "ADD_SKU_IMPORT_DRAFT",
    payload: { skuImportDraft },
  };
}

export function editSkuImportDrafts(skuImportDrafts: FormProduct[], status: SkuImportDraftState): EditSkuImportDraftAction {
  return {
    type: "EDIT_SKU_IMPORT_DRAFT",
    payload: { skuImportDrafts: skuImportDrafts, status },
  };
}

// service products (salesforce)
export function setServiceProducts(serviceProducts: Product[]): SetServiceProductsAction {
  return {
    type: "SET_SERVICE_PRODUCTS",
    payload: { serviceProducts },
  };
}

// connections
export function setConnections(connections: ProductConnection[]): SetConnectionsAction {
  return {
    type: "SET_CONNECTIONS",
    payload: { connections },
  };
}

export function setFilteredConnections(filteredConnections: ProductConnection[]): SetFilteredConnectionsAction {
  return {
    type: "SET_FILTERED_CONNECTIONS",
    payload: { filteredConnections },
  };
}

export function setLocalParentChildProductsAction(localParentChildProducts: Product[]): SetLocalParentChildProductsAction {
  return {
    type: "SET_LOCAL_PARENT_CHILD_PRODUCTS",
    payload: { localParentChildProducts },
  };
}

export function reducer(state: ProductsState, action: ProductsActions): ProductsState {
  switch (action.type) {
    case "SET_INITIALIZED":
      return {
        ...state,
        initialized: action.payload.initialized,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.loadingKey]: action.payload.loading,
        },
      };
    case "SET_ERROR":
      return {
        ...state,
        error: {
          ...state.error,
          [action.payload.errorKey]: action.payload.error,
        },
      };
    case "SET_LOCAL_PRODUCTS":
      return {
        ...state,
        localProducts: action.payload.localProducts,
      };
    case "SET_REMOTE_FORM_PRODUCTS":
      return {
        ...state,
        remoteFormProducts: action.payload.remoteFormProducts,
      };
    case "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS":
      return {
        ...state,
        remoteFormProductsWithImported: action.payload.remoteFormProductsWithImports,
      };
    case "SET_FILTERED_LOCAL_PRODUCTS":
      return {
        ...state,
        filteredLocalProducts: action.payload.filteredLocalProducts,
      };
    case "SET_REMOTE_PRODUCTS":
      return {
        ...state,
        remoteProducts: action.payload.remoteProducts,
      };
    case "SET_FILTERED_REMOTE_PRODUCTS":
      return {
        ...state,
        filteredRemoteProducts: action.payload.filteredRemoteProducts,
      };
    case "SET_SERVICE_PRODUCTS":
      return {
        ...state,
        serviceProducts: action.payload.serviceProducts,
      };
    case "SET_CONNECTIONS":
      return {
        ...state,
        connections: action.payload.connections,
      };
    case "SET_FILTERED_CONNECTIONS":
      return {
        ...state,
        filteredConnections: action.payload.filteredConnections,
      };
    case "EDIT_SKU_IMPORT_DRAFT":
      return {
        ...state,
        skuImportDrafts: updateDrafts(state.skuImportDrafts, action.payload.skuImportDrafts, action.payload.status),
      };
    case "ADD_SKU_IMPORT_DRAFT":
      if (state?.skuImportDrafts?.length === 0) {
        return { ...state, skuImportDrafts: [action.payload?.skuImportDraft] };
      }
      if (state?.skuImportDrafts && state?.skuImportDrafts?.length > 0) {
        return { ...state, skuImportDrafts: updateDraftInState(state.skuImportDrafts, [action.payload.skuImportDraft]) };
      }
      return state;
    case "SET_SKU_IMPORT_DRAFTS":
      if (action.payload.skuImportDrafts.length === 0) {
        return {
          ...state,
          skuImportDrafts: [],
        };
      }
      return {
        ...state,
        skuImportDrafts: [...action.payload.skuImportDrafts],
      };
    case "SET_LOCAL_PARENT_CHILD_PRODUCTS":
      return {
        ...state,
        localParentChildProducts: action.payload.localParentChildProducts,
      };
    default:
      return state;
  }
}

export const updateDrafts = (
  skuDrafts: FormProduct[] | undefined,
  products: FormProduct[],
  status: EditSkuImportDraftAction["payload"]["status"]
) => {
  switch (status) {
    case "ACTIVE":
      return updateDraftInState(skuDrafts, products);
    case "DISCARDED":
      return removeDraftFromState(skuDrafts, products);
    case "PUBLISHED":
      return removeDraftFromState(skuDrafts, products);
    default:
      return skuDrafts;
  }
};

export const removeDraftFromState = (skuDrafts: FormProduct[] | undefined, products: FormProduct[]) => {
  if (!skuDrafts) return;
  const newDrafts = skuDrafts?.filter((draft) => products.some((product) => draft.id !== product.id));
  if (newDrafts.length === 0) {
    return [];
  }
  return [...newDrafts];
};

export const updateDraftInState = (skuDrafts: FormProduct[] | undefined, products: FormProduct[]) => {
  if (!skuDrafts || skuDrafts.length === 0) {
    return [...products];
  }
  const existingDrafts = skuDrafts.filter((draft) => products.some((product) => draft.id !== product.id));
  if (existingDrafts.length > 0 && skuDrafts) {
    return [...existingDrafts, ...products];
  }
  return [...products];
};
